import { createSelector } from 'reselect';
import { translator } from 'mage-i18n';
import { appState } from './utils';
import { getWidgetRegionFromQueryParam } from './widget';

export const optionsNameSpace = createSelector(appState, ({ options = {} }) => options);

export const getGoodsCategories = createSelector(
  optionsNameSpace,
  translator,
  ({ organizationCategories = [] }, t) => {
    const prefix = 'products-fe.options.goods_categories';
    return organizationCategories
      .map(({ key }) => ({ key, label: t(`${prefix}.${key}`) }))
      .filter(({ label }) => !!label)
      .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0));
  },
);

export const getSignupCountries = createSelector(
  optionsNameSpace,
  translator,
  ({ signupCountries = [] }, t) =>
    signupCountries
      .reduce((list, country) => {
        const label = t(`products-fe.countries.${country.key}`);
        if (label) {
          return [...list, { ...country, label }];
        }
        return list;
      }, [])
      .sort((a, b) => a.label.localeCompare(b.label)),
);

export const getCountries = createSelector(optionsNameSpace, translator, ({ countries = [] }, t) =>
  countries.map(country => ({
    ...country,
    label: t(`products-fe.countries.${country.key}`, {}, country.key),
  })),
);

export const getSellingCountriesPerRegion = createSelector(
  optionsNameSpace,
  getWidgetRegionFromQueryParam,
  ({ sellingCountries, sellingCountriesPerRegion }, widgetRegion) => {
    if (!sellingCountriesPerRegion || !widgetRegion) {
      return sellingCountries;
    }
    return sellingCountriesPerRegion[widgetRegion] || sellingCountries;
  },
);

export const getSellingCountries = createSelector(
  getSellingCountriesPerRegion,
  translator,
  (sellingCountries = [], t) =>
    sellingCountries.map(key => ({
      key,
      label: t(`products-fe.countries.${key}`, {}, key),
    })),
);

export const getUsStates = createSelector(optionsNameSpace, translator, ({ states = [] }, t) =>
  states.map(({ key }) => ({
    key,
    label: t(`products-fe.options.us_states.${key}`),
  })),
);

export const getRegistrationCourts = country =>
  createSelector(optionsNameSpace, ({ countrySettings }) => {
    if (
      Object.prototype.hasOwnProperty.call(countrySettings, country) &&
      countrySettings[country].registrationCourts
    ) {
      return countrySettings[country].registrationCourts;
    }
    return [];
  });

export const getAllCurrencies = createSelector(
  optionsNameSpace,
  translator,
  ({ currencies = [] }, t) =>
    currencies &&
    currencies.map(currency => ({
      ...currency,
      label: t(`products-fe.currencies.${currency.key}`, {}, currency.key),
    })),
);

export const getPaymentIntervalOptions = createSelector(
  optionsNameSpace,
  translator,
  ({ paymentIntervals = [] }, t) =>
    paymentIntervals.map(key => ({
      key,
      label: t(`products-fe.options.payment_intervals.${key}`),
    })),
);

export const getBusinessConcepts = createSelector(
  optionsNameSpace,
  translator,
  ({ businessConcepts = [] }, t) =>
    businessConcepts.map(({ value, countries }) => ({
      value,
      countries,
      label: t(`products-fe.options.business_concepts.${value}.label`),
      description: t(`products-fe.options.business_concepts.${value}.description`),
    })),
);

// MOE-4968 put One Klarna products first
export const getProductNames = createSelector(optionsNameSpace, ({ productNames = [] }) =>
  productNames.sort((a, b) => {
    if (a.key.includes('ONE_KLARNA') && !b.key.includes('ONE_KLARNA')) {
      return -1;
    }
    if (!a.key.includes('ONE_KLARNA') && b.key.includes('ONE_KLARNA')) {
      return 1;
    }
    return a.label.localeCompare(b.label);
  }),
);

// Partner support configuration used when rendering the partner widget
export const getPartnerCountryConfiguration = language =>
  createSelector(
    optionsNameSpace,
    ({ partnerCountryConfiguration }) =>
      partnerCountryConfiguration[language.toUpperCase()] || partnerCountryConfiguration.EN || {},
  );

export const getPartnerCountrySupportEmail = language =>
  createSelector(getPartnerCountryConfiguration(language), ({ supportEmail }) => supportEmail);

export const getPartnerCountrySupportPhone = language =>
  createSelector(getPartnerCountryConfiguration(language), ({ supportPhone }) => supportPhone);

export const getPartnerCountryOpeningHours = language =>
  createSelector(
    getPartnerCountryConfiguration(language),
    ({ openingHoursText }) => openingHoursText,
  );

const getCountriesOfType = type =>
  createSelector(getSignupCountries, (countries = []) => countries.filter(c => c.type === type));

export const getSupportedCountries = getCountriesOfType('SUPPORTED');
export const getManualCountries = getCountriesOfType('MANUAL');

const getKeysFromCountries = (countries = []) => {
  return countries.map(country => country.key);
};

export const getSupportedCountryKeys = createSelector(getSupportedCountries, getKeysFromCountries);

export const getManualCountryKeys = createSelector(getManualCountries, getKeysFromCountries);

export const getPartnerDirectoryLinks = createSelector(
  optionsNameSpace,
  ({ partnerDirectoryLinks = [] }) => partnerDirectoryLinks,
);

export const getSalesTeamInfos = createSelector(
  optionsNameSpace,
  ({ salesTeamInfos = [] }) => salesTeamInfos,
);

export const getCountryConfig = createSelector(
  optionsNameSpace,
  ({ countryConfig }) => countryConfig,
);

export const getRegionConfig = createSelector(optionsNameSpace, ({ regionConfig }) => regionConfig);

export const getForcedRegistrationIdCountries = createSelector(getCountryConfig, countryConfig =>
  Object.entries(countryConfig)
    .filter(([_, config]) => config.REGISTRATION_ID_KIND === 'FORCED')
    .map(([country, _]) => country),
);

export const getForcedVatIdCountries = createSelector(getCountryConfig, countryConfig =>
  Object.entries(countryConfig)
    .filter(([_, config]) => config.VAT_ID_KIND === 'FORCED')
    .map(([country, _]) => country),
);

export const getEnrichedVatCountries = createSelector(getCountryConfig, countryConfig =>
  Object.entries(countryConfig)
    .filter(([_, config]) => config.ENRICHES_VAT)
    .map(([country, _]) => country),
);

export const getCountriesWithDocuments = createSelector(getCountryConfig, countryConfig =>
  Object.entries(countryConfig)
    .filter(([_, config]) => !!config.DOCUMENTS)
    .map(([country]) => country),
);
